import * as React from "react";
import { SideNav } from "../common/nav/side/SideNav.tsx";
import { BottomNav } from "../common/nav/bottom/BottomNav.tsx";
import { scrollToTopSignal } from "../common/nav/nav.tsx";
import { RequireSignIn } from "./RequireSignIn.tsx";
import "./layout.css";

interface Props {
  children?: React.ReactNode;
  /** Which page is showing. A new one starts at the top; the reader opening over it doesn't. */
  pageKey: string;
  /** The reader's open over the page, which stays put underneath so you come back to your place */
  covered?: boolean;
}

// The app's frame. Wider than a phone: the sidebar and the page beside it. On a phone: the page,
// with the bottom nav along the bottom. Either way it's the page that scrolls, not the window.
export const MainPageLayout: React.FC<Props> = ({ children, pageKey, covered }) => {
  const mainRef = React.useRef<HTMLElement>(null);
  const shellRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(
    () => scrollToTopSignal.add(() => mainRef.current?.scrollTo({ top: 0, behavior: "smooth" })),
    [],
  );

  React.useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pageKey]);

  // Under the reader, nothing here should take focus or clicks (React 18 has no `inert` prop)
  React.useEffect(() => {
    shellRef.current?.toggleAttribute("inert", !!covered);
  }, [covered]);

  return (
    <RequireSignIn>
      <div className="shell" ref={shellRef} aria-hidden={covered}>
        <aside className="shell-sidebar">
          <SideNav />
        </aside>
        <main className="shell-main" ref={mainRef}>
          <div className="shell-content">{children}</div>
        </main>
        <BottomNav />
      </div>
    </RequireSignIn>
  );
};
